// SPDX-License-Identifier: AGPL-3.0-or-later

import {CompanionMicrophone} from '@app/features/companion/audio/CompanionMicrophone';
import * as CompanionCommands from '@app/features/companion/commands/CompanionCommands';
import CompanionSessionStore from '@app/features/companion/state/CompanionSessionStore';
import Keybind from '@app/features/input/state/InputKeybind';
import {Logger} from '@app/features/platform/utils/AppLogger';
import MediaEngine, {useMediaEngineVersion} from '@app/features/voice/engine/MediaEngineFacade';
import {getEffectiveAudioState} from '@app/features/voice/engine/VoiceEffectiveAudioState';
import {
	getLocalPublicationMediaStreamTrack,
	getPrimaryLocalMicrophonePublication,
} from '@app/features/voice/engine/VoiceTrackPublicationUtils';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {
	getActiveInputDeviceLabel,
	resolveVoiceProcessingFromStateForDeviceLabel,
} from '@app/features/voice/utils/VoiceProcessingProfile';
import {reaction, runInAction} from 'mobx';
import {useEffect, useState} from 'react';

const logger = new Logger('CompanionRuntime');

function microphoneMuted(): boolean {
	return (
		getEffectiveAudioState().effectiveMute ||
		(Keybind.isPushToTalkEffective() && !Keybind.pushToTalkHeld) ||
		(Keybind.isPushToMuteEffective() && Keybind.pushToMuteHeld)
	);
}

export function CompanionRuntime() {
	const version = useMediaEngineVersion();
	const [audio] = useState(
		() =>
			new CompanionMicrophone(
				(level, label) => {
					CompanionSessionStore.setInputState(
						microphoneMuted(),
						level,
						getActiveInputDeviceLabel(VoiceSettings) ?? label,
					);
				},
				(error, endSession) => {
					logger.error('Companion audio input failed', {error, sessionId: CompanionSessionStore.session?.session_id});
					void CompanionSessionStore.handleAudioFailure(error, endSession);
				},
				(blocked) =>
					runInAction(() => {
						CompanionSessionStore.playbackBlocked = blocked;
					}),
			),
	);
	useEffect(() => {
		CompanionSessionStore.setAudioController(audio);
		return () => audio.disconnect();
	}, [audio]);
	useEffect(() => {
		const dispose = reaction(
			() => {
				const profile = resolveVoiceProcessingFromStateForDeviceLabel(
					VoiceSettings,
					getActiveInputDeviceLabel(VoiceSettings),
				);
				return {
					muted: microphoneMuted(),
					capture: {
						...(VoiceSettings.inputDeviceId !== 'default' ? {deviceId: {exact: VoiceSettings.inputDeviceId}} : {}),
						echoCancellation: profile.echoCancellation,
						noiseSuppression: profile.browserNoiseSuppression,
						autoGainControl: profile.autoGainControl,
						channelCount: 1,
					},
					// Read gain/gate/backend too so processing updates without reacquiring hardware.
					volume: VoiceSettings.inputVolume,
					outputVolume: VoiceSettings.outputVolume,
					outputDeviceId: VoiceSettings.outputDeviceId,
					profile,
					vad: VoiceSettings.getVadThreshold(),
				};
			},
			(state) => {
				audio.configureOutput(state.outputVolume / 100, state.outputDeviceId);
				const publication = MediaEngine.room
					? getPrimaryLocalMicrophonePublication(MediaEngine.room.localParticipant)
					: null;
				const roomTrack = publication ? getLocalPublicationMediaStreamTrack(publication) : null;
				audio.updateState({
					muted: state.muted,
					roomTrack,
					capture: state.capture,
					captureKey: JSON.stringify(state.capture),
				});
				CompanionSessionStore.setInputState(
					state.muted,
					CompanionSessionStore.inputLevel,
					getActiveInputDeviceLabel(VoiceSettings) ?? '',
				);
			},
			{fireImmediately: true},
		);
		// Reconcile state even if room events do not change observable settings.
		return dispose;
	}, [audio, version]);

	useEffect(() => {
		let disposed = false;
		let pending = false;
		const poll = async () => {
			const session = CompanionSessionStore.session;
			if (
				disposed ||
				pending ||
				!session ||
				!CompanionSessionStore.hasActiveSession ||
				CompanionSessionStore.isStarting ||
				CompanionSessionStore.isStopping ||
				CompanionSessionStore.isPausing
			)
				return;
			pending = true;
			const controlRevision = CompanionSessionStore.controlRevision;
			try {
				const detail = await CompanionCommands.getSession(session.session_id);
				if (
					!disposed &&
					controlRevision === CompanionSessionStore.controlRevision &&
					CompanionSessionStore.session?.session_id === session.session_id &&
					!CompanionSessionStore.isPausing &&
					!CompanionSessionStore.isStopping &&
					detail.session.status
				) {
					CompanionSessionStore.handleSessionState({
						session_id: session.session_id,
						status: detail.session.status,
						activity: detail.session.activity,
						speakers: Object.keys(detail.roles),
						scene_id: detail.session.scene_id ?? undefined,
						error: detail.session.last_error ?? undefined,
					});
				}
			} catch {
				/* Gateway events remain the primary delivery path. */
			} finally {
				pending = false;
			}
		};
		const timer = setInterval(() => void poll(), 5000);
		return () => {
			disposed = true;
			clearInterval(timer);
		};
	}, []);
	return null;
}
